import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, Layers, Briefcase, ShieldAlert, FileText,
  Settings, Users, Palette, LogOut, Plus, ListChecks, Sparkles, UserCheck, BarChart3, Gauge, CalendarRange, Stamp, Database, LayoutTemplate, ShieldCheck, Building2, Cloud, FileCheck, CreditCard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEntitlements } from "@/context/EntitlementsContext";
import { useBranding } from "@/context/BrandingContext";
import { resolveUrl } from "@/lib/api";

const TABS = [
  { key: "home", label: "Home", path: "/dashboard" },
  { key: "delivery", label: "Delivery", path: "/projects", match: ["/projects", "/approvals", "/controls"] },
  { key: "structure", label: "Structure", path: "/portfolios", match: ["/portfolios", "/programmes"] },
  { key: "insight", label: "Insight", path: "/reports", match: ["/reports", "/resources", "/assistant", "/compliance"] },
  { key: "admin", label: "Admin", path: "/admin", match: ["/admin"], roles: ["admin", "pmo"] },
];

const GROUPS = {
  home: [
    {
      name: "Overview",
      buttons: [
        { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", id: "rb-dashboard" },
        { label: "My Projects", icon: FolderKanban, path: "/projects", id: "rb-myprojects" },
      ],
    },
  ],
  delivery: [
    {
      name: "Projects",
      buttons: [
        { label: "All Projects", icon: FolderKanban, path: "/projects", id: "rb-projects" },
        { label: "New Project", icon: Plus, path: "/projects?new=1", id: "rb-new-project" },
      ],
    },
    {
      name: "Controls",
      buttons: [
        { label: "Risks", icon: ShieldAlert, path: "/controls/risks", id: "rb-raid", module: "risks" },
        { label: "Status", icon: FileText, path: "/controls/status-reports", id: "rb-status" },
        { label: "Approvals", icon: Stamp, path: "/approvals", id: "rb-approvals", module: "approvals", feature: "approvals_stage_gates" },
      ],
    },
  ],
  structure: [
    {
      name: "Hierarchy",
      buttons: [
        { label: "Portfolios", icon: Briefcase, path: "/portfolios", id: "rb-portfolios" },
        { label: "Programmes", icon: Layers, path: "/programmes", id: "rb-programmes" },
      ],
    },
  ],
  insight: [
    {
      name: "Analysis",
      buttons: [
        { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", id: "rb-insight-dashboard" },
        { label: "Reports", icon: BarChart3, path: "/reports", id: "rb-reports", module: "reports" },
        { label: "Compliance", icon: ShieldCheck, path: "/compliance", id: "rb-compliance", module: "compliance", feature: "compliance_scorecards" },
      ],
    },
    {
      name: "Planning",
      buttons: [{ label: "Resources", icon: Gauge, path: "/resources", id: "rb-resources", module: "resources" }],
    },
    {
      name: "Automation",
      buttons: [{ label: "Assistant", icon: Sparkles, path: "/assistant", id: "rb-assistant", module: "assistant", feature: "ai_assistant" }],
    },
  ],
  admin: [
    {
      name: "Configuration",
      buttons: [
        { label: "Lists", icon: ListChecks, path: "/admin", id: "rb-lists" },
        { label: "Users", icon: Users, path: "/admin/users", id: "rb-users" },
        { label: "Demo Data", icon: Database, path: "/admin/demo-data", id: "rb-demo-data" },
        { label: "Billing", icon: CreditCard, path: "/admin/billing", id: "rb-billing" },
      ],
    },
    {
      name: "Governance",
      buttons: [
        { label: "Frameworks", icon: ShieldCheck, path: "/admin/frameworks", id: "rb-frameworks", feature: "framework_templating" },
        { label: "Sector", icon: Building2, path: "/admin/sector", id: "rb-sector", feature: "sector_presets" },
        { label: "Approvers", icon: UserCheck, path: "/admin/approvers", id: "rb-approvers", feature: "approvals_stage_gates" },
        { label: "Reporting Periods", icon: CalendarRange, path: "/admin/periods", id: "rb-periods", feature: "reporting_periods" },
        { label: "BI Feeds", icon: Database, path: "/admin/bi", id: "rb-bi", feature: "bi_feeds" },
        { label: "Plan Templates", icon: LayoutTemplate, path: "/admin/schedule-templates", id: "rb-templates", feature: "plan_templates" },
      ],
    },
    {
      name: "Security & Trust",
      buttons: [
        { label: "AI Engine", icon: Cloud, path: "/admin/ai-engine", id: "rb-ai-engine", feature: "byo_azure_ai" },
        { label: "Data Egress", icon: ShieldCheck, path: "/admin/ai", id: "rb-ai-egress", feature: "ai_assurance" },
        { label: "AI Assurance", icon: FileCheck, path: "/admin/ai-assurance", id: "rb-ai-assurance", feature: "ai_assurance" },
      ],
    },
    {
      name: "Brand",
      buttons: [{ label: "White-label", icon: Palette, path: "/admin/branding", id: "rb-branding", feature: "white_label" }],
    },
  ],
};

export const Ribbon = ({ minimal = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { branding, t, moduleOn } = useBranding();
  const { can } = useEntitlements();

  const activeTab =
    TABS.find((x) => (x.match || [x.path]).some((m) => location.pathname.startsWith(m)))?.key || "home";
  const [tab, setTab] = React.useState(activeTab);
  React.useEffect(() => setTab(activeTab), [activeTab]);

  const visibleTabs = TABS.filter((x) => !x.roles || x.roles.includes(user?.role));

  return (
    <header className="sticky top-0 z-40">
      <div
        className="flex items-center justify-between px-4 pt-2"
        style={{ background: `linear-gradient(100deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)` }}
      >
        <div className="flex items-center gap-3">
          {branding.logo_url ? (
            <img
              src={resolveUrl(branding.logo_url)}
              alt="logo"
              data-testid="brand-logo"
              className="h-8 w-auto max-w-[140px] object-contain"
            />
          ) : (
            <div
              data-testid="brand-logo"
              className="flex h-8 w-8 items-center justify-center rounded bg-white/15 text-sm font-bold text-white"
            >
              {(branding.product_name || "PH").slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="leading-tight">
            <div className="font-display text-sm font-bold text-white">{branding.product_name}</div>
            <div data-testid="brand-org-name" className="text-[10px] uppercase tracking-wider text-white/60">{branding.org_name}</div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="insights-assistant-btn"
            onClick={() => navigate("/assistant")}
            className={`${minimal || !can("ai_assistant") ? "hidden" : "hidden sm:flex"} items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90 transition hover:bg-white/20`}
          >
            <Sparkles size={13} /> Insights Assistant
          </button>
          <div className="text-right leading-tight">
            <div data-testid="current-user-name" className="text-xs font-semibold text-white">
              {user?.name}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-white/60">{user?.role}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => logout().then(() => navigate("/login"))}
            className="rounded p-1.5 text-white/70 transition hover:bg-white/15 hover:text-white"
            title="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>

      {!minimal && (
      <div
        className="flex items-center gap-1 overflow-x-auto px-3"
        style={{ background: `linear-gradient(100deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)` }}
      >
        {visibleTabs.map((tb) => (
          <button
            key={tb.key}
            data-testid={`ribbon-tab-${tb.key}`}
            onClick={() => {
              setTab(tb.key);
              navigate(tb.path);
            }}
            className={`ribbon-tab ${tab === tb.key ? "ribbon-tab-active" : ""}`}
          >
            {t(tb.label)}
          </button>
        ))}
      </div>
      )}

      {!minimal && (
      <div className="flex items-stretch gap-0 overflow-x-auto border-b border-slate-300 bg-[#f5f7fa] py-2">
        {(GROUPS[tab] || GROUPS.home)
          .map((g) => ({ ...g, buttons: g.buttons.filter((b) => (!b.module || moduleOn(b.module)) && can(b.feature)) }))
          .filter((g) => g.buttons.length > 0)
          .map((g) => (
          <div key={g.name} className="ribbon-group">
            <div className="flex flex-col items-center">
              <div className="flex items-end gap-1">
                {g.buttons.map((b) => (
                  <button
                    key={b.id}
                    data-testid={b.id}
                    onClick={() => navigate(b.path)}
                    className="ribbon-btn"
                  >
                    <b.icon size={18} style={{ color: "var(--brand-primary)" }} />
                    <span className="whitespace-nowrap">{t(b.label)}</span>
                  </button>
                ))}
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">{t(g.name)}</div>
            </div>
          </div>
        ))}
        <div className="flex flex-1 items-center justify-end px-4">
          <button
            data-testid="ribbon-settings"
            onClick={() => navigate(user?.role === "admin" || user?.role === "pmo" ? "/admin" : "/dashboard")}
            className="ribbon-btn"
          >
            <Settings size={18} style={{ color: "var(--brand-primary)" }} />
            <span>Settings</span>
          </button>
        </div>
      </div>
      )}
    </header>
  );
};
